import React, { useState } from 'react';
import { FileText, Sparkles, Loader2, RotateCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

// Panel beneath a phone-call note's audio player. Two on-demand AI
// actions:
//   - Generate Transcription → Whisper turns the recording into text
//   - Generate Summary       → GPT-4o-mini condenses the transcript
// Results are persisted on the Note's customFields server-side, so a
// second visit shows them instantly without paying again. The
// Regenerate buttons hit the same endpoints with ?force=1 to bypass
// the cache.
// basePath switches the API namespace: 'voip' (default, human VoIP
// calls) or 'phone-agent' (AI receptionist calls) — both expose the
// same transcribe/summarize contract.
//
// inlineTranscript: the transcript already exists (AI calls capture it
// live), so the transcript button is a pure show/hide toggle — no
// Whisper call, no cost. Only Generate Summary talks to an LLM.
const CallTranscriptPanel = ({ noteId, initialCf = {}, basePath = 'voip', inlineTranscript = false }) => {
  const [transcript, setTranscript] = useState(initialCf.transcript || '');
  const [language, setLanguage] = useState(initialCf.transcript_language || null);
  const [summary, setSummary] = useState(initialCf.summary || '');
  const [loadingT, setLoadingT] = useState(false);
  const [loadingS, setLoadingS] = useState(false);
  const [showFullTranscript, setShowFullTranscript] = useState(false);

  const runTranscribe = async (force = false) => {
    setLoadingT(true);
    try {
      const { data } = await api.post(
        `/${basePath}/transcribe/${noteId}${force ? '?force=1' : ''}`
      );
      setTranscript(data.transcript || '');
      setLanguage(data.language || null);
      if (!data.cached) toast.success('Transcription ready');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Transcription failed');
    } finally {
      setLoadingT(false);
    }
  };

  const runSummarize = async (force = false) => {
    setLoadingS(true);
    try {
      const { data } = await api.post(
        `/${basePath}/summarize/${noteId}${force ? '?force=1' : ''}`
      );
      setSummary(data.summary || '');
      // The summarize endpoint also auto-transcribes when needed, so
      // pick up the transcript it returned if we didn't have one yet.
      if (data.transcript && !transcript) setTranscript(data.transcript);
      if (data.language && !language) setLanguage(data.language);
      if (!data.cached) toast.success('Summary ready');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Summary failed');
    } finally {
      setLoadingS(false);
    }
  };

  return (
    <div className="mt-3 space-y-3">
      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2">
        {inlineTranscript ? (
          transcript && (
            <button
              type="button"
              onClick={() => setShowFullTranscript((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-cyan-300 text-cyan-800 hover:bg-cyan-50"
            >
              <FileText size={14} />
              {showFullTranscript ? 'Hide Transcript' : 'Show Transcript'}
            </button>
          )
        ) : !transcript ? (
          <button
            type="button"
            onClick={() => runTranscribe(false)}
            disabled={loadingT}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-green-300 text-green-800 hover:bg-green-100 disabled:opacity-50"
          >
            {loadingT ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            {loadingT ? 'Transcribing…' : 'Generate Transcription'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => runTranscribe(true)}
            disabled={loadingT}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            title="Regenerate transcription"
          >
            {loadingT ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
            Re-transcribe
          </button>
        )}
        {!summary ? (
          <button
            type="button"
            onClick={() => runSummarize(false)}
            disabled={loadingS}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-purple-300 text-purple-800 hover:bg-purple-100 disabled:opacity-50"
          >
            {loadingS ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loadingS ? 'Summarizing…' : 'Generate Summary'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => runSummarize(true)}
            disabled={loadingS}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            title="Regenerate summary"
          >
            {loadingS ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
            Re-summarize
          </button>
        )}
      </div>

      {/* Summary card — appears above transcript because it's the
          headline; the operator can skim it before opening the full
          transcript. Purple matches the Sparkles AI icon language used
          elsewhere in the app. */}
      {summary && (
        <div className="rounded-md border border-purple-200 bg-purple-50 p-3">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-purple-700 uppercase tracking-wide mb-1">
            <Sparkles size={12} />
            Summary
          </div>
          <p className="text-sm text-purple-900 whitespace-pre-wrap leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Transcript — collapsed by default to keep the timeline scannable;
          click "Show full transcript" to expand. Long mono-spaced calls
          (sales scripts, medical intake) can run hundreds of words and
          would otherwise dominate the page. */}
      {transcript && (inlineTranscript ? showFullTranscript : true) && (
        <div className="rounded-md border border-gray-200 bg-white p-3">
          {inlineTranscript ? (
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 uppercase tracking-wide">
              <FileText size={12} />
              Transcript
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowFullTranscript((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 uppercase tracking-wide hover:text-gray-900"
            >
              <FileText size={12} />
              Transcription
              {language && <span className="font-normal text-gray-500 normal-case">· {language}</span>}
              <span className="ml-1 text-gray-400 normal-case">({showFullTranscript ? 'hide' : 'show'})</span>
            </button>
          )}
          {showFullTranscript && (
            <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{transcript}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CallTranscriptPanel;
